<div class="min-h-screen bg-gray-50 p-8">
    <div class="max-w-3xl mx-auto space-y-6">

        <div>
            <h1 class="text-2xl font-bold text-gray-900">ACH Transaction Details — Demo</h1>
            <p class="mt-1 text-sm text-gray-500">Select a scenario to preview the modal with different data configurations.</p>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Scenarios</h2>
            <div class="flex flex-wrap gap-3">
                @for (s of scenarios; track s.key) {
                    <button
                        (click)="selectScenario(s.key)"
                        class="px-4 py-2 rounded-md text-sm font-medium transition-all text-white"
                        [ngClass]="{
                            'ring-2 ring-offset-2 font-bold': selectedScenario === s.key,
                            'opacity-75 hover:opacity-100': selectedScenario !== s.key,
                            'bg-green-600 ring-green-600': s.color === 'green',
                            'bg-red-600 ring-red-600': s.color === 'red',
                            'bg-blue-600 ring-blue-600': s.color === 'blue',
                            'bg-amber-600 ring-amber-600': s.color === 'amber',
                            'bg-purple-600 ring-purple-600': s.color === 'purple'
                        }">
                        {{ s.label }}
                    </button>
                }
            </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Permission Toggles</h2>
            <div class="flex flex-wrap gap-6">
                <label class="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input type="checkbox" [(ngModel)]="canExport"
                        class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    Can Export
                </label>
            </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Error States</h2>
            <div class="flex flex-wrap gap-3">
                @for (e of errorScenarios; track e.key) {
                    <button
                        (click)="selectErrorScenario(e.id)"
                        class="px-4 py-2 rounded-md text-sm font-medium transition-all bg-red-600 text-white opacity-75 hover:opacity-100">
                        {{ e.label }}
                    </button>
                }
            </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Foundation Utilities (US-2.0.2)</h2>

            <h3 class="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2">Status Badges — All 19 Statuses</h3>
            <div class="flex flex-wrap gap-2">
                @for (s of allStatuses; track s) {
                    <ach-status-badge [status]="s" />
                }
            </div>

            <h3 class="text-xs font-semibold text-gray-500 uppercase mt-6 mb-2">NegativeCurrencyPipe</h3>
            <table class="text-sm border-collapse">
                <thead>
                    <tr class="text-left text-gray-500">
                        <th class="pr-6 pb-1">Input</th>
                        <th class="pb-1">Output</th>
                    </tr>
                </thead>
                <tbody class="font-mono">
                    @for (val of currencySamples; track $index) {
                        <tr>
                            <td class="pr-6 py-0.5 text-gray-500">{{ val === null ? 'null' : val }}</td>
                            <td class="py-0.5" [class.text-red-600]="val !== null && val < 0">{{ val | negativeCurrency }}</td>
                        </tr>
                    }
                </tbody>
            </table>

            <h3 class="text-xs font-semibold text-gray-500 uppercase mt-6 mb-2">formatUserName()</h3>
            <div class="text-sm space-y-1 font-mono">
                <div><span class="text-gray-500">formatUserName("Jane Smith", "Wellfit") →</span> {{ formatUserName('Jane Smith', 'Wellfit') }}</div>
                <div><span class="text-gray-500">formatUserName("Jane Smith") →</span> {{ formatUserName('Jane Smith') }}</div>
                <div><span class="text-gray-500">formatUserName("") →</span> "{{ formatUserName('') }}"</div>
            </div>
        </div>

        @if (!useMockData) {
        <div class="bg-white rounded-lg shadow p-6 border-l-4 border-emerald-500">
            <h2 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Integration Mode</h2>
            <p class="text-xs text-gray-500 mb-3">Mock data is OFF. Create a session, enter a Transaction ID, and open the modal with real BFF data.</p>

            <div class="space-y-4">
                <div class="flex items-center gap-3">
                    <button
                        (click)="createSession()"
                        [disabled]="sessionStatus === 'loading' || sessionStatus === 'ready'"
                        class="px-4 py-2 bg-emerald-600 text-white rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        @if (sessionStatus === 'loading') {
                            Creating...
                        } @else {
                            Create Session
                        }
                    </button>
                    <span class="text-sm">
                        @if (sessionStatus === 'ready') {
                            <span class="text-emerald-600 font-mono">{{ sessionId }}</span>
                        } @else if (sessionStatus === 'error') {
                            <span class="text-red-600">{{ sessionError }}</span>
                        }
                    </span>
                </div>

                @if (sessionStatus === 'ready') {
                <div class="flex items-end gap-3 pt-2 border-t border-gray-100">
                    <div class="flex-1">
                        <label class="block text-xs font-medium text-gray-600 mb-1">Transaction ID</label>
                        <input type="text" [(ngModel)]="realTransactionId" placeholder="e.g. ACHSEED2-FF61-05"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
                    </div>
                    <button
                        (click)="openWithRealData()"
                        [disabled]="!realTransactionId"
                        class="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors shadow disabled:opacity-40 disabled:cursor-not-allowed">
                        Open with Real Data
                    </button>
                </div>
                }
            </div>
        </div>
        }

        <div>
            <button
                (click)="openModal()"
                class="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow">
                Open Modal
            </button>
        </div>

    </div>
</div>

<ach-transaction-details-modal
    [transactionId]="selectedTransactionId"
    [hidden]="hidden"
    [sessionId]="sessionId"
    [canExport]="canExport"
    (transactionSelected)="onTransactionSelected($event)" />